import { useTranslation } from "react-i18next";

/**
 * i18next uses short language codes ("sv", "en"). The Strapi CMS's
 * registered locale codes are confirmed (via direct GraphQL query
 * against cms-test) to be the same short codes — "sv" and "en", not
 * "sv-SE". This mapping stays explicit rather than assumed in case
 * that ever diverges.
 */
const CMS_LOCALE_BY_LANGUAGE: Record<string, string> = {
  sv: "sv",
  en: "en",
};

export const DEFAULT_CMS_LOCALE = CMS_LOCALE_BY_LANGUAGE.sv;

/**
 * Maps an i18next language code (e.g. "sv", "en-US") to the matching
 * Strapi locale code. Falls back to the default CMS locale for any
 * language i18next knows about that the CMS doesn't (yet) have a
 * mapping for.
 */
export const toCmsLocale = (language?: string): string => {
  if (!language) return DEFAULT_CMS_LOCALE;
  const base = language.split("-")[0].toLowerCase();
  return CMS_LOCALE_BY_LANGUAGE[base] ?? DEFAULT_CMS_LOCALE;
};

/**
 * Returns the CMS locale matching the current i18next language, and
 * re-renders the calling component whenever the language changes
 * (react-i18next's useTranslation subscribes to i18next's
 * languageChanged event), so any GraphQL query using this as a
 * variable will reactively refetch via Apollo.
 */
export const useCmsLocale = (): string => {
  const { i18n } = useTranslation();
  return toCmsLocale(i18n.language);
};
